import { Component, Input } from '@angular/core';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';

// Interactive HWB mixer for the HWB post, all sliders, no boxes. A hue slider picks the
// pure hue, then a whiteness slider and a blackness slider mix white and black into it,
// which is exactly what the hwb() notation writes down. Two swatches show the pure hue
// and the mixed result side by side, so dragging W or B visibly pulls the mix away from
// the untouched pure hue. When W + B reaches 100 the hue runs out of room and the mix
// collapses to the grey W / (W + B), mirroring the CSS normalization. The starting hue,
// whiteness and blackness come from the post's own color via [hex] (kept as floats so
// the exact hex reproduces). The post marks the mount point with
// <!--HWB-START-->...<!--HWB-END--> around a static fallback; the page swaps this live
// component in. Same design language as the value-ladder widget.
@Component( {
  selector: 'app-hwb-mixer',
  templateUrl: './hwb-mixer.component.html',
  styleUrls: [ './hwb-mixer.component.scss' ],
} )
export class HwbMixerComponent {

  @Input() heading = 'Try It Yourself';

  // Hue, whiteness and blackness all come from the post color. Defaults to the HWB
  // post's sakura pink until a hex is bound.
  hue = 330;
  white = 54.9;
  black = 12.94;
  private _hex = '#DE8CB5';

  @Input() set hex( value: string ) {
    const rgb = this.parseHex( value );
    if ( !rgb ) {
      return;
    }
    this._hex = '#' + String( value ).replace( /^#/, '' ).toUpperCase();
    const [ h, w, b ] = this.rgbToHwb( rgb );
    this.hue = h;
    this.white = w;
    this.black = b;
  }
  get hex(): string {
    return this._hex;
  }

  private tracked = false;

  constructor( private analyticsService: AnalyticsService ) {
  }

  // The untouched pure hue: whiteness and blackness both at zero.
  get pureHex(): string {
    return this.hwbToHex( this.hue, 0, 0 );
  }

  // The mix at the current hue/whiteness/blackness.
  get mixHex(): string {
    return this.hwbToHex( this.hue, this.white, this.black );
  }

  // The mix written the way CSS writes it (space separated, per CSS Color 4).
  get hwbString(): string {
    return `hwb(${ this.hueLabel } ${ this.whiteLabel }% ${ this.blackLabel }%)`;
  }

  // True once white and black together fill the whole mix, leaving no room for the hue.
  get overflowed(): boolean {
    return this.white + this.black >= 100;
  }

  // Tints the slider thumbs with the live mix so the controls read in context.
  get thumb(): string {
    return this.mixHex;
  }

  // The whiteness slider ramps the pure hue toward white, the blackness slider toward
  // black, so each track shows what pushing it further does.
  get whiteTrack(): string {
    return `linear-gradient( 90deg, ${ this.pureHex }, #ffffff )`;
  }
  get blackTrack(): string {
    return `linear-gradient( 90deg, ${ this.pureHex }, #000000 )`;
  }

  // Whole-number readouts (the values themselves stay precise).
  get hueLabel(): number {
    return Math.round( this.hue );
  }
  get whiteLabel(): number {
    return Math.round( this.white );
  }
  get blackLabel(): number {
    return Math.round( this.black );
  }

  onHue( value: string ): void {
    this.hue = this.clamp( parseInt( value, 10 ), 0, 359 );
    this.track();
  }

  onWhite( value: string ): void {
    this.white = this.clamp( parseInt( value, 10 ), 0, 100 );
    this.track();
  }

  onBlack( value: string ): void {
    this.black = this.clamp( parseInt( value, 10 ), 0, 100 );
    this.track();
  }

  private track(): void {
    if ( this.tracked ) {
      return;
    }
    this.tracked = true;
    this.analyticsService.logEvent( 'Blog Widget Used', { widget: 'hwb-mixer' } );
  }

  private clamp( v: number, lo: number, hi: number ): number {
    if ( isNaN( v ) ) {
      return lo;
    }
    return Math.max( lo, Math.min( hi, v ) );
  }

  private parseHex( text: string ): [ number, number, number ] | null {
    const h = String( text || '' ).trim().replace( /^#/, '' );
    if ( !/^[0-9a-fA-F]{6}$/.test( h ) ) {
      return null;
    }
    return [
      parseInt( h.slice( 0, 2 ), 16 ),
      parseInt( h.slice( 2, 4 ), 16 ),
      parseInt( h.slice( 4, 6 ), 16 ),
    ];
  }

  // [r,g,b] (0..255) to [hue 0..360, whiteness 0..100, blackness 0..100], unrounded so
  // the post color reproduces exactly.
  private rgbToHwb( rgb: [ number, number, number ] ): [ number, number, number ] {
    const r = rgb[ 0 ] / 255, g = rgb[ 1 ] / 255, b = rgb[ 2 ] / 255;
    const mx = Math.max( r, g, b ), mn = Math.min( r, g, b ), d = mx - mn;
    let h = 0;
    if ( d !== 0 ) {
      if ( mx === r ) {
        h = ( ( g - b ) / d ) % 6;
      } else if ( mx === g ) {
        h = ( b - r ) / d + 2;
      } else {
        h = ( r - g ) / d + 4;
      }
      h = ( h * 60 + 360 ) % 360;
    }
    return [ h, mn * 100, ( 1 - mx ) * 100 ];
  }

  // HWB to hex: the pure hue's channels are scaled into the room left after white and
  // black take their share, then lifted by the whiteness. When W + B >= 100 the hue
  // cancels out and the color is the grey W / (W + B), per the CSS normalization.
  private hwbToHex( h: number, w: number, b: number ): string {
    const wN = w / 100, bN = b / 100;
    const to = ( v: number ) => Math.round( v * 255 ).toString( 16 ).padStart( 2, '0' );
    if ( wN + bN >= 1 ) {
      const grey = wN / ( wN + bN );
      return ( '#' + to( grey ) + to( grey ) + to( grey ) ).toUpperCase();
    }
    const c = 1;
    const x = c * ( 1 - Math.abs( ( ( h / 60 ) % 2 ) - 1 ) );
    let r = 0, g = 0, bl = 0;
    if ( h < 60 ) { r = c; g = x; }
    else if ( h < 120 ) { r = x; g = c; }
    else if ( h < 180 ) { g = c; bl = x; }
    else if ( h < 240 ) { g = x; bl = c; }
    else if ( h < 300 ) { r = x; bl = c; }
    else { r = c; bl = x; }
    const scale = 1 - wN - bN;
    const mix = ( v: number ) => v * scale + wN;
    return ( '#' + to( mix( r ) ) + to( mix( g ) ) + to( mix( bl ) ) ).toUpperCase();
  }
}
